import React, { useCallback, useState } from 'react';

// Comps
import { Link } from '@arctheme-components/elements/Link/Link';
import { Image as ResponsiveImage } from '@arctheme-components/elements/Image/Image';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { Headings } from '@arctheme-components/elements/Headings/Headings';

// Hooks
import { useCurrentTextStyle } from '@src/hooks/useCurrentTextStyle';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

// Types
import type {
  Order,
  OrderLocales,
  Settings,
  OrderLineItem,
  OrderShippingMethod,
  OrderTransactionPaymentDetails,
  DiscountAllocation
} from './types';

// Shopify helpers
import shopify from '../../../../../js/shopify';
import helpers from 'frontend/js/helpers';

interface AccountOrderProps {
  order: Order;
  locales: OrderLocales;
  settings: Settings;
  onClickClose: () => void;
}

const AccountOrder: React.FC<AccountOrderProps> = ({ order, locales, settings, onClickClose }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const { isMobile } = useResponsiveBreakpoints();
  const [showAllItems, setShowAllItems] = useState(false);

  const handleClickBack = (event: React.ClickEvent<HTMLLinkElement>) => {
    event.preventDefault();
    onClickClose();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const {
    orderNumber,
    createdAt,
    fulfillmentStatus,
    paymentStatus,
    lineItems,
    total,
    fulfillment,
    transactions,
    customer,
    shippingAddress,
    billingAddress,
    shippingMethods
  } = order;
  const paymentDetails: OrderTransactionPaymentDetails | null = transactions?.[0]?.paymentDetails || null;

  const decodeHtml = (html: string) => {
    return new DOMParser().parseFromString(html, "text/html").documentElement.textContent;
  }

  // Helper function to get responsive text styles using the hook
  const getResponsiveTextStyle = (styleKey: string) => {
    const styles = settings?.textStyles?.[styleKey];
    if (!styles) return '';

    return getCurrentTextStyle(styles.default, styles.enableResponsive, styles.mobile, styles.tablet, styles.ultrawide);
  };

  const formatMoney = useCallback((cents: number) => {
    const dollars = cents / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }, []);

  return (
    <div className='account-order mx-auto'>
      <div className={'flex justify-between items-center mb-4'}>
        {/* Back Button */}
        <Link
          link={' '}
          classes='inline-flex items-center gap-2 link link-md'
          onClick={handleClickBack}
        >
          <i className='icon-arrow-back text-[20px]'></i>
          <span className='l2--medium'>{settings?.backToOrders || 'Back to Order History'}</span>
        </Link>
      </div>

      <div className='flex flex-col lg:flex-row gap-3'>
        {/* Left Column - Status & Details */}
        <div className='lg:basis-[52%] space-y-[16px]'>
           {/* Order Header */}
        <div className='py-4 lg:py-5 px-3 lg:px-4'>
          <div>
            <Paragraph
              text={`Order ${orderNumber}`}
              classes={`${isMobile ? 'h2' : 'h2'}`}
            />
          </div>
        </div>
          {/* Delivery Status */}
          <div className='bg-white py-4 lg:py-5 px-3 lg:px-4'>
            <div className={`inline-flex items-center mb-1 ${getResponsiveTextStyle('status')}`}>
              {helpers.capitalizeFirst(fulfillmentStatus)}
            </div>
            <p className={`${getResponsiveTextStyle('date')} text-[#808080] mb-2`}>{formatDate(createdAt)}</p>
          </div>

          {/* Payment Status */}
          <div className='bg-white py-4 lg:py-5 px-3 lg:px-4'>
            <div className={`inline-flex items-center mb-1 ${getResponsiveTextStyle('status')}`}>
              {helpers.capitalizeFirst(paymentStatus)}
            </div>
            <p className={`${getResponsiveTextStyle('date')} text-[#808080] mb-2`}>{formatDate(createdAt)}</p>
            {paymentDetails?.creditCardCompany && (
              <p className={`${isMobile ? 'b9--medium' : 'b7--medium'}`}>
                Credit Card ({paymentDetails.creditCardCompany})
              </p>
            )}
          </div>

          {/* Order Details */}
          <div className='bg-white py-3 lg:py-5 px-3 lg:px-4'>
            <h3 className={`${getResponsiveTextStyle('orderDetails')} mb-4`}>
              {settings?.texts?.orderDetails || 'Order Details'}
            </h3>

            <div className='grid grid-cols-2 gap-x-4 gap-y-6'>
              <div className='col-span-2'>
                <h4 className={`${isMobile ? 'b2--bold' : 'b2--bold'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.contactInfo || 'Contact information'}
                </h4>
                <p className={`${isMobile ? 'b3' : 'b3'}`}>{customer.name}</p>
                <p className={`${isMobile ? 'b3' : 'b3'}`}>{customer.email}</p>
              </div>

              {/* <div>
                <h4 className={`${isMobile ? 'b8--medium' : 'b7--medium'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.payment || 'Payment'}
                </h4>
                {paymentDetails?.creditCardCompany && (
                  <p className={`${isMobile ? 'b9--medium' : 'b8--medium'}`}>
                    {paymentDetails.creditCardCompany} ending in {paymentDetails.creditCardLast4}
                  </p>
                )}
              </div> */}

              <div>
                <h4 className={`${isMobile ? 'b2--bold' : 'b2--bold'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.shippingAddress || 'Shipping address'}
                </h4>
                <p
                  className='b3'
                  dangerouslySetInnerHTML={{ __html: decodeHtml(shippingAddress) }}
                ></p>
              </div>

              <div>
                <h4 className={`${isMobile ? 'b2--bold' : 'b2--bold'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.billingAddress || 'Billing address'}
                </h4>
                <p
                  className='b3'
                  dangerouslySetInnerHTML={{ __html: decodeHtml(billingAddress) }}
                ></p>
              </div>

              {/* <div>
                <h4 className={`${isMobile ? 'b2--bold' : 'b2--bold'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.shippingMethod || 'Shipping method'}
                </h4>
                {shippingMethods.map((method: OrderShippingMethod, index: number) => (
                  <div key={index}>
                    <p className={`${isMobile ? 'b3' : 'b3'}`}>
                      {method.title} {method.price}
                    </p>
                  </div>
                ))}
              </div> */}

              {/* <div>
                <h4 className={`${isMobile ? 'b2--bold' : 'b2--bold'} mb-[8px] text-[#878787]`}>
                  {settings?.texts?.company || 'Company'}
                </h4>
                <p className={`${isMobile ? 'b3' : 'b3'}`}>-</p>
              </div> */}
            </div>
          </div>
        </div>

        {/* Right Column - Order Summary */}
        <div className='lg:basis-[48%]'>
          <div className='bg-white rounded-3xl py-5 px-4 lg:px-6'>
            <h3 className={`${getResponsiveTextStyle('summary')} mb-5`}>{settings?.texts?.summary || 'Summary'}</h3>

            {/* Product Items */}
            <div className='space-y-4 mb-10'>
              {(showAllItems ? lineItems : lineItems.slice(0, 2)).map((item: OrderLineItem, index: number) => {
                const { product } = item || {};

                if (!product) return null;

                const filteredProductOptions = Object.entries(item.product_options).filter(([key, value]) => value);
                const displayedItems = showAllItems ? lineItems : lineItems.slice(0, 2);
                const borderClass = index === displayedItems.length - 1 ? '' : 'mb-3 pb-3 border-[#EDEDED] border-b';

                return (
                  <div
                    key={item.id}
                    className={`flex gap-[16px] ${borderClass}`}
                  >
                    <div className='basis-[26%] min-w-[78px] flex-shrink-0'>
                      {(() => {
                        // Enhanced image URL detection with Storefront API enriched data
                        const imageUrl = product.featured_image;

                        return imageUrl ? (
                          <a href={`/products/${product.handle}`}>
                            <ResponsiveImage
                              imageUrl={imageUrl}
                              alt={product.title}
                              classes='w-full h-full object-cover'
                              width='64'
                              height='64'
                            />
                          </a>
                        ) : (
                          <div className='w-full h-full flex items-center justify-center'>
                            <span className='b3'>No Image</span>
                          </div>
                        );
                      })()}
                    </div>

                    <div className='flex-1'>
                      <a href={`/products/${product.handle}`}>
                        <h4 className={`${isMobile ? 'b3--bold' : 'b3--bold'}`}>{product.title}</h4>
                      </a>

                      {filteredProductOptions && filteredProductOptions.length > 0 && (
                        <div className='mt-3 space-y-1'>
                          {filteredProductOptions.map(([key, value], optionIndex: number) => (
                            <div
                              key={key}
                              className={`product-option ${isMobile ? 'b3' : 'b3'} text-[#1C1B1B]`}
                            >
                              {helpers.capitalizeFirst(key)}: {value}
                            </div>
                          ))}
                        </div>
                      )}

                      <p className={`${isMobile ? 'b3' : 'b3'} text-[#1C1B1B] mt-3`}>
                        {settings?.texts?.qty || 'Qty:'} {item.quantity}
                      </p>

                      {item.discount_allocations && item.discount_allocations.length > 0 && (
                        <div className='mt-6 space-y-1'>
                          {item.discount_allocations.map((discount: DiscountAllocation, discountIndex: number) => (
                            <div
                              key={discountIndex}
                              className={`line-discount-item flex items-center justify-between ${
                                isMobile ? 'b3' : 'b3'
                              }`}
                            >
                              <span>{discount.title}</span>
                              <span>-{formatMoney(discount.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {item.fulfillment.trackingNumber && item.fulfillment.trackingUrl && (
                        <div className='mt-3 b8--medium'>
                          {settings?.texts?.trackingNumber || 'Tracking number:'}
                          <a
                            href={item.fulfillment.trackingUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            {item.fulfillment.trackingNumber}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className='text-right'>
                      {item.originalPrice !== item.price && (
                        <s className={`text-[#878787] block mb-1 ${isMobile ? 'b9--medium' : 'b8--medium'}`}>
                          {item.originalPrice}
                        </s>
                      )}

                      <p className={`${isMobile ? 'b3' : 'b3'}`}>{item.price}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Show All Products Button */}
            {!showAllItems && lineItems.length > 2 && (
              <div className='mb-6'>
                <button
                  onClick={() => setShowAllItems(true)}
                  className='link link-md'
                >
                  Show All Products ({lineItems.length})
                </button>
              </div>
            )}

            {/* Order Totals */}
            <div className='space-y-[16px] pt-6 pb-4'>
              <div className={`flex justify-between b3 ${getResponsiveTextStyle('orderSummary')}`}>
                <span>{settings?.texts?.subtotal || 'Subtotal'}</span>
                <span className='b3'>{order.orderSubtotal}</span>
              </div>

              {order.totalDiscount && (
                <div className={`flex justify-between b3 ${getResponsiveTextStyle('orderSummary')}`}>
                  <span>{settings?.texts?.saved || 'Saved'}</span>
                  <span className='b3'>-{order.totalDiscount}</span>
                </div>
              )}

              <div className={`flex justify-between b3 ${getResponsiveTextStyle('orderSummary')}`}>
                <span>{settings?.texts?.shipping || 'Shipping'}</span>
                <span className='b3'>{order.shippingPrice}</span>
              </div>

              <div className='flex justify-between b3 pt-2 border-t'>
                <span>{settings?.texts?.summaryTotal || 'Total'}</span>
                <span>{total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountOrder;
